import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    TableRows, Insights, GridOn, Movie, PlayCircle,
    Map as MapIcon, ToggleOn, ToggleOff, Analytics, ArrowOutward, QueryStats,
} from '@mui/icons-material';
import { getMatchById } from '../../services/MatchService';
import { getPlayerMatchesByGameId, getSwingBreakdown } from '../../services/PlayerMatchService';
import { getClipsByMatch, getDemoStatus, DemoStatusResponse } from '../../services/HighlightsService';
import { getReplayGameIds } from '../../services/ReplayService';
import { Match } from '../../models/Match';
import { PlayerMatch } from '../../models/PlayerMatch';
import { HighlightClip } from '../../models/HighlightClip';
import { SwingBreakdown } from '../../models/SwingBreakdown';
import { mapMappings } from '../../Constants';
import Loading from '../../components/Loading';
import ReplayViewer from '../MatchPage/Replay/ReplayViewer';
import { useReplayPosition, writeReplayParams } from '../MatchPage/Replay/replayLink';
import Match2Header from './Match2Header';
import Match2Scoreboard from './Match2Scoreboard';
import Match2Rounds from './Match2Rounds';
import Match2Detailed from './Match2Detailed';
import Match2Matrix from './Match2Matrix';
import Match2Veto from './Match2Veto';
import Match2Clips from './Match2Clips';
import Match2Swing from './Match2Swing';
import { Side, Match2Tab, compareRows, aggregatePlayerRows, anyHasR3 } from './matchStats';
import styles from './MatchPage2.module.css';

const isPlayed = (m: Match): boolean => m.team1score + m.team2score > 0;

const tabKeys: Match2Tab[] = ['overview', 'detailed', 'matrix', 'clips', 'replay', 'swing'];
const parseTab = (value: string | null): Match2Tab =>
    tabKeys.find(k => k === value) ?? 'overview';

// de_mirage* -> mirage, so a shared link reads ?map=mirage
const mapSlug = (m: Match): string => m.mapName.replace('*', '').replace(/^de_/, '').toLowerCase();
const matchesMapSlug = (m: Match, q: string): boolean => {
    const wanted = q.replace(/^de_/, '').toLowerCase();
    return mapSlug(m) === wanted;
};

// The match page, served at /match/:matchId. Replaced the original at the season 33 launch;
// /match2 redirects here. Implements Match.dc.html.
const MatchPage2 = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const { t } = useTranslation('pages');
    const [isLoading, setIsLoading] = useState(true);
    const [matches, setMatches] = useState<Match[] | null>(null);
    const [playersByGame, setPlayersByGame] = useState<Record<string, PlayerMatch[]>>({});
    const [searchParams, setSearchParams] = useSearchParams();
    // Read once: after this the URL follows the state, not the other way around.
    const initialMap = useRef(searchParams.get('map'));
    const [tab, setTab] = useState<Match2Tab>(() => parseTab(searchParams.get('tab')));
    const [mapIdx, setMapIdx] = useState(0); // 0 = all maps, i >= 1 = matches[i - 1]
    const [side, setSide] = useState<Side>('all');
    const [eco, setEco] = useState(false);
    // ?player= (written on the Swing tab) so a shared swing link opens on that player
    const [selectedSteamId, setSelectedSteamId] = useState<string | null>(() => searchParams.get('player'));
    const [clips, setClips] = useState<HighlightClip[]>([]);
    const [demoStatus, setDemoStatus] = useState<DemoStatusResponse | null>(null);
    const [replayGameIds, setReplayGameIds] = useState<string[]>([]);
    // Round + moment the replay tab opens on (?round=&t=), kept here so it outlives the viewer.
    const replayLink = useReplayPosition(searchParams);
    // Swing tab: per-event swing per played map; null = that map has no stored breakdown.
    const [swingByGame, setSwingByGame] = useState<Record<string, SwingBreakdown | null>>({});

    const fetchMatches = useCallback(async () => {
        try {
            if (!matchId) throw new Error('No matchId provided');
            const matchesData = await getMatchById(matchId);
            if (!matchesData) throw new Error('No data found');
            setMatches(matchesData);
            if (initialMap.current) {
                const idx = matchesData.findIndex(m => matchesMapSlug(m, initialMap.current!));
                if (idx >= 0) setMapIdx(idx + 1);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [matchId]);

    useEffect(() => {
        fetchMatches();
    }, [fetchMatches]);

    // One request per played map returns all 10 players of that game.
    useEffect(() => {
        if (!matches) return;
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                matches.filter(isPlayed).map(async (m) => {
                    try {
                        return [m.gameid, await getPlayerMatchesByGameId(m.gameid)] as const;
                    } catch (err) {
                        console.error(err);
                        return [m.gameid, [] as PlayerMatch[]] as const;
                    }
                })
            );
            if (!cancelled) setPlayersByGame(Object.fromEntries(entries));
        })();
        return () => { cancelled = true; };
    }, [matches]);

    // Swing breakdowns, for the Swing tab and the replay's live odds
    useEffect(() => {
        if (!matches) return;
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                matches.filter(isPlayed).map(async (m) => {
                    try {
                        return [m.gameid, await getSwingBreakdown(m.gameid)] as const;
                    } catch (err) {
                        console.error(err);
                        return [m.gameid, null] as const;
                    }
                })
            );
            if (!cancelled) setSwingByGame(Object.fromEntries(entries));
        })();
        return () => { cancelled = true; };
    }, [matches]);

    // Clips + demo status (for the tab count badge and the clips tab itself)
    useEffect(() => {
        if (!matchId) return;
        getClipsByMatch(matchId).then(setClips).catch(() => setClips([]));
        getDemoStatus(matchId).then(setDemoStatus).catch(() => setDemoStatus(null));
    }, [matchId]);

    // Replay availability (match level)
    useEffect(() => {
        if (!matchId) return;
        getReplayGameIds(matchId).then(setReplayGameIds);
    }, [matchId]);

    // The view is shareable: the active tab, the picked map and the replay round/moment live in
    // the URL (?tab=replay&map=mirage&round=7&t=42), so a pasted link lands where the sender was.
    useEffect(() => {
        if (!matches) return;
        const next = new URLSearchParams(searchParams);
        if (tab === 'overview') next.delete('tab'); else next.set('tab', tab);
        const picked = matches.length > 1 && mapIdx > 0 ? matches[mapIdx - 1] : null;
        if (picked) next.set('map', mapSlug(picked)); else next.delete('map');
        writeReplayParams(next, tab === 'replay', replayLink.position);
        if (tab === 'swing' && selectedSteamId) next.set('player', selectedSteamId); else next.delete('player');
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, [tab, mapIdx, matches, searchParams, setSearchParams, replayLink.position, selectedSteamId]);

    // Round strip / clip -> the replay tab on that map and round.
    const { openAt } = replayLink;
    const openReplayAt = useCallback((gameId: string, round: number, focusSteamId: string | null = null) => {
        if (matches && matches.length > 1) {
            const idx = matches.findIndex(m => m.gameid === gameId);
            if (idx >= 0) setMapIdx(idx + 1);
        }
        openAt(round, focusSteamId);
        setTab('replay');
    }, [matches, openAt]);

    const selectedMatch = useMemo((): Match | null => {
        if (!matches || matches.length === 0) return null;
        if (matches.length === 1) return matches[0]; // Bo1: the single map is always "selected"
        return mapIdx > 0 ? matches[mapIdx - 1] ?? null : null;
    }, [matches, mapIdx]);

    // Rows per team for the current selection: a single map's rows, or the
    // "Alle maps" aggregation across the series (sums + rounds-weighted rates).
    const rowsForTeam = useCallback((teamNo: 1 | 2): PlayerMatch[] => {
        if (!matches) return [];
        if (selectedMatch) {
            const ids = (teamNo === 1 ? selectedMatch.team1playerids : selectedMatch.team2playerids) || [];
            return (playersByGame[selectedMatch.gameid] || []).filter(p => ids.includes(p.steamId));
        }
        const byPlayer: Record<string, PlayerMatch[]> = {};
        for (const m of matches.filter(isPlayed)) {
            const ids = (teamNo === 1 ? m.team1playerids : m.team2playerids) || [];
            for (const p of playersByGame[m.gameid] || []) {
                if (ids.includes(p.steamId)) (byPlayer[p.steamId] = byPlayer[p.steamId] || []).push(p);
            }
        }
        return Object.values(byPlayer).map(aggregatePlayerRows);
    }, [matches, selectedMatch, playersByGame]);

    const sortedTeam1 = useMemo(() => rowsForTeam(1).sort(compareRows(side)), [rowsForTeam, side]);
    const sortedTeam2 = useMemo(() => rowsForTeam(2).sort(compareRows(side)), [rowsForTeam, side]);

    // Entering the replay tab with "Alle maps" selected: jump to the first map
    // that has a replay (the viewer is per-game).
    useEffect(() => {
        if (tab !== 'replay' || !matches || matches.length === 1) return;
        if (selectedMatch && replayGameIds.includes(selectedMatch.gameid)) return;
        const idx = matches.findIndex(m => replayGameIds.includes(m.gameid));
        if (idx >= 0) setMapIdx(idx + 1);
    }, [tab, matches, replayGameIds, selectedMatch]);

    // Entering the swing tab with "Alle maps" selected: jump to the first map with a breakdown. A picked map without
    // one stays picked and says so.
    useEffect(() => {
        if (tab !== 'swing' || !matches || matches.length === 1 || selectedMatch) return;
        const idx = matches.findIndex(m => swingByGame[m.gameid]);
        if (idx >= 0) setMapIdx(idx + 1);
    }, [tab, matches, swingByGame, selectedMatch]);

    // Default detailed-tab selection: top-rated player still on the boards.
    useEffect(() => {
        if (tab !== 'detailed') return;
        const all = [...sortedTeam1, ...sortedTeam2];
        if (all.length === 0) return;
        if (!selectedSteamId || !all.some(r => r.steamId === selectedSteamId)) {
            setSelectedSteamId(all[0].steamId);
        }
    }, [tab, sortedTeam1, sortedTeam2, selectedSteamId]);


    if (isLoading) {
        return (
            <div className="container text-center text-light">
                <Loading />
            </div>
        );
    }
    if (!matches || matches.length === 0 || !matchId) return <div>No data available.</div>;

    const first = matches[0];
    if (!first.team1 || !first.team2) {
        // Defensive: uploaded/edge matches can have unresolved teams
        return (
            <div className="text-center my-3 text-light">
                <h5>{first.mapName}</h5>
                <div>{first.team1?.teamName ?? 'Team 1'} {first.team1score} – {first.team2score} {first.team2?.teamName ?? 'Team 2'}</div>
            </div>
        );
    }

    const hasNoDemoData = matches.some(m => m.mapName.includes('*'));
    const demoTabs = first.season >= 29 && !hasNoDemoData;
    const hasReplay = replayGameIds.length > 0;
    const hasSwing = Object.values(swingByGame).some(Boolean);

    // Header score: ALWAYS the series result for a multi-map series (map switching must not
    // change it); the single map's rounds for a Bo1. Board scores may still be per-map below.
    let headerTeam1Score: number;
    let headerTeam2Score: number;
    if (matches.length > 1) {
        headerTeam1Score = matches.filter(m => isPlayed(m) && m.team1score > m.team2score).length;
        headerTeam2Score = matches.filter(m => isPlayed(m) && m.team2score > m.team1score).length;
    } else {
        headerTeam1Score = first.team1score;
        headerTeam2Score = first.team2score;
    }

    // Team-board score: the selected map's rounds, or the series result on "Alle maps".
    let team1Score: number;
    let team2Score: number;
    if (matches.length > 1 && !selectedMatch) {
        team1Score = headerTeam1Score;
        team2Score = headerTeam2Score;
    } else {
        const m = selectedMatch ?? first;
        team1Score = m.team1score;
        team2Score = m.team2score;
    }

    const mapClipCount = selectedMatch
        ? clips.filter(c => (c.mapName || '') === selectedMatch.mapName.replace('*', '')).length
        : clips.length;

    const tabDefs: { key: Match2Tab; label: string; icon: React.ReactNode; show: boolean; count?: number }[] = [
        { key: 'overview', label: t('match2.tabOverview'), icon: <TableRows sx={{ fontSize: 19 }} />, show: true },
        { key: 'detailed', label: t('match2.tabDetailed'), icon: <Insights sx={{ fontSize: 19 }} />, show: demoTabs },
        { key: 'matrix', label: t('match2.tabMatrix'), icon: <GridOn sx={{ fontSize: 19 }} />, show: demoTabs },
        { key: 'clips', label: t('match2.tabClips'), icon: <Movie sx={{ fontSize: 19 }} />, show: demoTabs, count: mapClipCount },
        { key: 'replay', label: t('match2.tabReplay'), icon: <PlayCircle sx={{ fontSize: 19 }} />, show: hasReplay },
        { key: 'swing', label: t('match2.tabSwing'), icon: <QueryStats sx={{ fontSize: 19 }} />, show: demoTabs && hasSwing },
    ];
    const activeTab: Match2Tab = tabDefs.some(d => d.key === tab && d.show) ? tab : 'overview';

    // "Analyse" is the one item in the row that leaves the page. It hands the analysis tool this
    // match, which is all it needs to offer both sides as the subject and find its way back here;
    // the picked map rides along, so a series still on "Alle maps" opens on the tool's map picker.
    const analyseParams = new URLSearchParams({ match: matchId });
    if (selectedMatch) analyseParams.set('map', selectedMatch.mapName.replace('*', ''));
    const analyseHref = `/analyse?${analyseParams.toString()}`;

    // Pre-R3 matches (all players lack rating30): boards fall back to Rating 2.1/2.0,
    // and the eco toggle is hidden because eco stats come from the same engine. Default
    // to the modern layout until rows load so a modern match doesn't flash the fallback.
    const totalRows = sortedTeam1.length + sortedTeam2.length;
    const hasR3 = totalRows === 0 || anyHasR3([...sortedTeam1, ...sortedTeam2]);

    const showMapPills = matches.length > 1;
    const showSide = demoTabs && (activeTab === 'overview' || activeTab === 'detailed' || activeTab === 'matrix' || activeTab === 'swing');
    const showEco = demoTabs && activeTab === 'overview' && hasR3;
    const showControls = showMapPills || showSide || showEco;

    const mapLabel = (m: Match) => mapMappings.get(m.mapName.replace('*', '')) ?? m.mapName.replace('*', '');

    const sideDefs: { key: Side; label: string; activeClass: string }[] = [
        { key: 'all', label: t('match2.sideBoth'), activeClass: styles.sidePillBoth },
        { key: 't', label: 'T', activeClass: styles.sidePillT },
        { key: 'ct', label: 'CT', activeClass: styles.sidePillCt },
    ];

    const replayMatch = activeTab === 'replay' ? selectedMatch : null;
    const swingMatch = activeTab === 'swing' ? selectedMatch : null;
    const swingData = swingMatch ? swingByGame[swingMatch.gameid] : null;
    const replayPlayers = replayMatch ? (playersByGame[replayMatch.gameid] || []) : [];

    return (
        <div className={styles.page}>
            <Match2Header
                matches={matches}
                selectedMatch={selectedMatch}
                team1Score={headerTeam1Score}
                team2Score={headerTeam2Score}
            />

            {hasNoDemoData && (
                <div className={styles.warnBanner}>
                    <strong>⚠️ Bemærk:</strong> Denne kamp mangler demo data. De viste statistikker er estimater baseret på tilgængelige data og kan have unøjagtigheder.
                </div>
            )}

            {/* Tabs */}
            <div className={styles.tabs}>
                {tabDefs.filter(d => d.show).map(d => (
                    <button
                        key={d.key}
                        className={`${styles.tab} ${activeTab === d.key ? styles.tabActive : ''}`}
                        onClick={() => setTab(d.key)}
                    >
                        {d.icon}
                        {d.label}
                        {d.count !== undefined && d.count > 0 && (
                            <span className={styles.tabCount}>{d.count}</span>
                        )}
                    </button>
                ))}
                {demoTabs && (
                    <Link
                        to={analyseHref}
                        className={`${styles.tab} ${styles.tabLink}`}
                        title={t('match2.analyseHint')}
                    >
                        <Analytics sx={{ fontSize: 19 }} />
                        {t('match2.tabAnalyse')}
                        <ArrowOutward sx={{ fontSize: 13 }} className={styles.tabLinkArrow} />
                    </Link>
                )}
            </div>

            {/* Controls: map pills | eco toggle + side toggle */}
            {showControls && (
                <div className={styles.controls}>
                    <div>
                        {showMapPills && (
                            <div className={styles.pillGroup}>
                                <button
                                    className={`${styles.pill} ${mapIdx === 0 ? styles.pillActive : ''}`}
                                    onClick={() => { setMapIdx(0); openAt(1); }}
                                >
                                    {t('match2.allMaps')}
                                </button>
                                {matches.map((m, i) => (
                                    <button
                                        key={m.gameid}
                                        className={`${styles.pill} ${mapIdx === i + 1 ? styles.pillActive : ''}`}
                                        onClick={() => { setMapIdx(i + 1); openAt(1); }}
                                    >
                                        <MapIcon sx={{ fontSize: 15 }} />
                                        {mapLabel(m)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={styles.controlsRight}>
                        {showEco && (
                            <button
                                className={`${styles.ecoBtn} ${eco ? styles.ecoBtnActive : ''}`}
                                title={t('stats:ecoAdjustedTooltip')}
                                onClick={() => setEco(e => !e)}
                            >
                                {eco ? <ToggleOn sx={{ fontSize: 17 }} /> : <ToggleOff sx={{ fontSize: 17 }} />}
                                {t('stats:ecoAdjusted')}
                            </button>
                        )}
                        {showSide && (
                            <div className={styles.sideGroup}>
                                {sideDefs.map(sd => (
                                    <button
                                        key={sd.key}
                                        className={`${styles.sidePill} ${side === sd.key ? sd.activeClass : ''}`}
                                        onClick={() => setSide(sd.key)}
                                    >
                                        {sd.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===== Overview ===== */}
            {activeTab === 'overview' && (
                <div className={styles.fadeIn} style={{ marginTop: 18 }}>
                    <Match2Scoreboard
                        team={first.team1}
                        score={team1Score}
                        rows={sortedTeam1}
                        side={side}
                        eco={eco && demoTabs && hasR3}
                        hasR3={hasR3}
                    />
                    {!hasNoDemoData && selectedMatch?.roundEnds && (
                        <Match2Rounds
                            roundEnds={selectedMatch.roundEnds}
                            team1={selectedMatch.team1}
                            team2Id={selectedMatch.team2?.teamId}
                            onRoundClick={replayGameIds.includes(selectedMatch.gameid)
                                ? (round) => openReplayAt(selectedMatch.gameid, round)
                                : undefined}
                        />
                    )}
                    <Match2Scoreboard
                        team={first.team2}
                        score={team2Score}
                        rows={sortedTeam2}
                        side={side}
                        eco={eco && demoTabs && hasR3}
                        hasR3={hasR3}
                    />
                    {first.vetoLog && (
                        <Match2Veto vetoLog={first.vetoLog} team1={first.team1} team2={first.team2} />
                    )}
                </div>
            )}

            {/* ===== Detailed ===== */}
            {activeTab === 'detailed' && (
                <Match2Detailed
                    team1={first.team1}
                    team2={first.team2}
                    team1Rows={sortedTeam1}
                    team2Rows={sortedTeam2}
                    side={side}
                    selectedSteamId={selectedSteamId}
                    onSelect={setSelectedSteamId}
                />
            )}

            {/* ===== Kill matrix ===== */}
            {activeTab === 'matrix' && (
                <Match2Matrix
                    team1={first.team1}
                    team2={first.team2}
                    team1Rows={sortedTeam1}
                    team2Rows={sortedTeam2}
                    side={side}
                />
            )}

            {/* ===== Clips ===== */}
            {activeTab === 'clips' && (
                <Match2Clips
                    matchId={matchId}
                    match={first}
                    selectedMatch={matches.length === 1 ? null : selectedMatch}
                    clips={clips}
                    demoStatus={demoStatus}
                    replayMapNames={matches.filter(m => replayGameIds.includes(m.gameid)).map(m => m.mapName.replace('*', ''))}
                    onViewInReplay={(clip) => {
                        const game = matches.find(m => m.mapName.replace('*', '') === clip.mapName);
                        if (game) openReplayAt(game.gameid, clip.roundNr, clip.steamId);
                    }}
                />
            )}

            {/* ===== Swing ===== */}
            {activeTab === 'swing' && swingMatch && (swingData ? (
                <Match2Swing
                    data={swingData}
                    rows={playersByGame[swingMatch.gameid] || []}
                    team1={swingMatch.team1 ?? first.team1}
                    team2={swingMatch.team2 ?? first.team2}
                    team1Ids={swingMatch.team1playerids || []}
                    roundEnds={swingMatch.roundEnds}
                    side={side}
                    selectedSteamId={selectedSteamId}
                    onSelect={setSelectedSteamId}
                />
            ) : (
                <div className={styles.warnBanner}>{t('match2.swing.noData')}</div>
            ))}

            {/* ===== Replay (reused as-is from the old page) ===== */}
            {activeTab === 'replay' && replayMatch && (
                <div className={styles.replayWrapper} style={{ marginTop: 18 }}>
                    <ReplayViewer
                        season={matches[0].season}
                        matchId={matchId}
                        gameId={replayMatch.gameid}
                        mapName={replayMatch.mapName}
                        playerNames={Object.fromEntries(
                            replayPlayers
                                .filter(p => p.steamId && p.account?.playerName)
                                .map(p => [p.steamId, p.account!.playerName])
                        )}
                        team1Name={replayMatch.team1?.teamName || 'Team 1'}
                        team2Name={replayMatch.team2?.teamName || 'Team 2'}
                        roundEnds={replayMatch.roundEnds}
                        team1Id={replayMatch.team1?.teamId}
                        team2Id={replayMatch.team2?.teamId}
                        team1SteamIds={replayMatch.team1playerids}
                        team1Logo={replayMatch.team1?.teamLogo}
                        team2Logo={replayMatch.team2?.teamLogo}
                        initialRound={replayLink.position.round}
                        initialTime={replayLink.position.time}
                        initialFocusSteamId={replayLink.position.focusSteamId}
                        onRoundChange={replayLink.onRoundChange}
                        swing={swingByGame[replayMatch.gameid] ?? null}
                    />
                </div>
            )}
        </div>
    );
};

export default MatchPage2;
